import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  LogOut, 
  FileText, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock,
  Edit,
  Trash2,
  Mail,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OfferDashboard = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/offers/`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        logout();
        navigate('/admin/login');
        return;
      }
      
      const data = await response.json();
      setOffers(data);
    } catch (err) {
      setError('Fehler beim Laden der Angebote');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Angebot wirklich löschen?')) return;
    
    try {
      await fetch(`${API_URL}/api/offers/${offerId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchOffers();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleResend = async (offerId) => {
    try {
      const response = await fetch(`${API_URL}/api/offers/${offerId}/send`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        alert('Angebot erfolgreich gesendet!');
        fetchOffers();
      } else {
        const data = await response.json();
        alert(`Fehler: ${data.detail}`);
      }
    } catch (err) {
      alert('Fehler beim Senden');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Entwurf', color: 'bg-gray-500', icon: Clock },
      sent: { label: 'Gesendet', color: 'bg-blue-500', icon: Send },
      accepted: { label: 'Angenommen', color: 'bg-green-500', icon: CheckCircle },
      declined: { label: 'Abgelehnt', color: 'bg-red-500', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/images/alpen-pro-logo-new.png" 
              alt="Alpen Pro" 
              className="h-10"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Angebotsverwaltung</h1>
              <p className="text-sm text-gray-500">Agent Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchOffers}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Gesamt', value: offers.length, color: 'bg-gray-100 text-gray-700' },
            { label: 'Entwürfe', value: offers.filter(o => o.status === 'draft').length, color: 'bg-gray-100 text-gray-700' },
            { label: 'Angenommen', value: offers.filter(o => o.status === 'accepted').length, color: 'bg-green-100 text-green-700' },
            { label: 'Abgelehnt', value: offers.filter(o => o.status === 'declined').length, color: 'bg-red-100 text-red-700' }
          ].map((stat, idx) => (
            <Card key={idx} className={stat.color}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{stat.value}</p>
                <p className="text-sm">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Alle Angebote</h2>
          <Button
            onClick={() => navigate('/admin/offers/new')}
            className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700"
            data-testid="new-offer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neues Angebot erstellen
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Offers List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-2 text-gray-500">Laden...</p>
          </div>
        ) : offers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Angebote vorhanden</h3>
              <p className="text-gray-500 mb-4">Erstellen Sie Ihr erstes Angebot</p>
              <Button
                onClick={() => navigate('/admin/offers/new')}
                className="bg-gradient-to-r from-cyan-600 to-purple-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neues Angebot
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {offers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((offer) => (
              <Card key={offer.id} className="hover:shadow-md transition-shadow" data-testid={`offer-card-${offer.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {offer.customer_name}
                        </h3>
                        {getStatusBadge(offer.status)}
                      </div>
                      <div className="text-sm text-gray-500 space-y-1">
                        {offer.company_name && (
                          <p>Firma: {offer.company_name}</p>
                        )}
                        <p>E-Mail: {offer.customer_email}</p>
                        <p>
                          Typ: {offer.offer_type === 'package' ? offer.package_type : 'Freitext'}
                        </p>
                        <p>Gültig bis: {formatDate(offer.valid_until)}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-bold text-cyan-600 mb-2">
                        {formatPrice(offer.price)}
                      </p>
                      <p className="text-xs text-gray-400 mb-3">
                        Erstellt: {formatDate(offer.created_at)}
                      </p>
                      
                      <div className="flex gap-2 justify-end">
                        {offer.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/offers/edit/${offer.id}`)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleResend(offer.id)}
                              className="bg-cyan-600 hover:bg-cyan-700"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Senden
                            </Button>
                          </>
                        )}
                        {offer.status === 'sent' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResend(offer.id)}
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Erneut senden
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(offer.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default OfferDashboard;
